/**
 * La reconstruction — le plan de chaque niveau rebâti en direct, et manipulable.
 *
 * Un lancement déroule les niveaux l'un après l'autre : pour chacun, le plan se
 * construit sous les yeux, local par local, avec ses PAROIS et ses OUVERTURES.
 * Le plan n'est pas qu'une image : on le survole, on le fouille (un local ouvre
 * son détail), on l'allège (la légende masque une couche), on l'agrandit
 * (molette + glissé), et de là on chiffre un local.
 *
 * Aucun modèle : la géométrie vient des outils de relevé (build_rooms,
 * get_walls_for_room, survey_openings_for_room). Tout ce qui s'affiche — aires
 * recalculées, chevauchements, incertitudes — est un fait vérifiable poussé par
 * le flux, jamais une valeur inventée à l'écran.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { api, ErreurApi } from "../../api/client";
import type { NiveauReconstruction } from "../../api/types";
import { Avis } from "../../composants/avis";
import { Chargement, Echec } from "../../composants/etats";
import { useDonnees } from "../../composants/donnees";
import { naviguer } from "../../routeur";
import { Chiffrage } from "./Chiffrage";

// ── WKT → tracé SVG (l'axe Y du plan monte, celui du SVG descend) ───────────

function anneaux(wkt: string): number[][][] {
  const out: number[][][] = [];
  for (const [, corps] of wkt.matchAll(/\(([^()]+)\)/g)) {
    const ring: number[][] = [];
    for (const pt of corps!.split(",")) {
      const [x, y] = pt.trim().split(/\s+/).map(Number);
      if (Number.isFinite(x) && Number.isFinite(y)) ring.push([x!, y!]);
    }
    if (ring.length >= 2) out.push(ring);
  }
  return out;
}
const cheminPoly = (rings: number[][][]): string =>
  rings.filter((r) => r.length >= 3)
    .map((r) => "M" + r.map(([x, y]) => `${x},${-y!}`).join(" L") + " Z").join(" ");
const cheminLigne = (rings: number[][][]): string =>
  rings.map((r) => "M" + r.map(([x, y]) => `${x},${-y!}`).join(" L")).join(" ");

/** Les arêtes d'une géométrie WKT, en repère ÉCRAN (Y descendant) : `[x1,y1,x2,y2]`.
 *  Sert à orienter une ouverture le long de la paroi qui la porte. */
function aretes(wkt: string): number[][] {
  const out: number[][] = [];
  for (const ring of anneaux(wkt)) {
    for (let i = 0; i + 1 < ring.length; i++) {
      out.push([ring[i]![0]!, -ring[i]![1]!, ring[i + 1]![0]!, -ring[i + 1]![1]!]);
    }
  }
  return out;
}

/** Le centre d'un contour, en repère ÉCRAN (Y descendant) : la moyenne des
 *  sommets du plus grand anneau. Sert à poser le marqueur d'un local annoté —
 *  pas une étiquette (on les a retirées), un simple point de repère. */
function centreEcran(wkt: string): [number, number] | null {
  const rings = anneaux(wkt);
  if (!rings.length) return null;
  const grand = rings.reduce((a, b) => (b.length > a.length ? b : a), rings[0]!);
  let sx = 0, sy = 0;
  for (const [x, y] of grand) { sx += x!; sy += -y!; }
  return [sx / grand.length, sy / grand.length];
}

/** L'arête du contour la PLUS PROCHE du point : son angle, la distance, et le
 *  POINT PROJETÉ dessus. Une ouverture est posée sur le pourtour du local ; on
 *  l'y ALIGNE et l'y COLLE (au point projeté) plutôt que de la laisser flotter à
 *  côté. `dist` grand = pourtour lointain (donnée douteuse) → l'appelant retombe
 *  sur un point. Point et arêtes sont en repère écran. */
function orienter(edges: number[][], x: number, y: number):
    { angle: number; dist: number; px: number; py: number } {
  let best = Infinity, angle = 0, px = x, py = y;
  for (const [x1, y1, x2, y2] of edges) {
    const dx = x2! - x1!, dy = y2! - y1!;
    const long2 = dx * dx + dy * dy || 1e-9;
    let t = ((x - x1!) * dx + (y - y1!) * dy) / long2;
    t = Math.max(0, Math.min(1, t));
    const qx = x1! + t * dx, qy = y1! + t * dy;
    const d = Math.hypot(x - qx, y - qy);
    if (d < best) { best = d; angle = Math.atan2(dy, dx); px = qx; py = qy; }
  }
  return { angle, dist: best, px, py };
}

// ── Modèle : tout ce que le flux porte pour un local ────────────────────────

interface Paroi {
  wkt: string; role?: string; type?: string; materiau?: string | null;
  longueur_m?: number | null; partage_m?: number | null;
}
/** Un corps de gros œuvre du PLAN ENTIER (mur/cloison), hors de tout local :
 *  la structure du parvis, des cages, des rampes — dessinée sous les locaux. */
interface MurPlan { wkt: string; type?: string; calque?: string; }
interface Ouverture {
  genre?: string; largeur_m?: number | null; hauteur_m?: number | null;
  position_m?: number[] | null; station_m?: number | null;
}
interface Piece {
  nom?: string; aire_m2?: number | null; perimetre_m?: number | null; wkt?: string;
  cloison_manquante?: boolean; noms?: string[];
  // Une pièce d'un ensemble est ANNOTABLE comme un local : sa propre clé
  // géométrique, le nom d'origine, l'annotation et le drapeau de renommage.
  cle?: string; nom_source?: string; renomme?: boolean; annotation?: string;
}
/** Un escalier : une grappe de tracés regroupés, annotable comme un local — sa
 *  propre clé, une emprise (aire/périmètre), un nombre de traits (indicateur de taille). */
interface Escalier {
  cle?: string; nom?: string; nom_source?: string; renomme?: boolean;
  annotation?: string; traces?: string[]; wkt?: string;
  aire_m2?: number | null; perimetre_m?: number | null; traits?: number | null;
}
interface SousZone { materiau?: string; aire_m2?: number | null; etiquette?: string; }
interface Hauteur { genre?: string; valeur_m?: number | null; }
/** Un REPÈRE d'espace : un nom posé au plan SANS local (parvis, rampe,
 *  circulation) — ni contour, ni surface. Annotable comme un escalier (sa propre
 *  clé, ancrée), sans aire ni périmètre : de la géométrie à poser, jamais un
 *  nombre. `hauteurs` : les hsp écrites juste à côté (pour l'infobulle/détail). */
interface Repere {
  cle?: string; nom?: string; nom_source?: string; renomme?: boolean;
  annotation?: string; texte?: string; famille?: string; est_rampe?: boolean;
  anchor?: number[] | null; hauteurs?: Hauteur[];
}
/** Un texte quelconque du plan (nom, hauteur, finition, note DCE, symbole),
 *  révélé au SURVOL — de l'affichage, jamais un métré. */
interface Etiquette { texte?: string; anchor?: number[] | null; calque?: string; }
interface Local {
  rang: number; pas?: number; sur?: number;
  // La clé géométrique (stable) sur laquelle vivent notes et renommages ; le
  // nom d'origine avant renommage ; l'annotation du métreur, s'il y en a une.
  cle?: string; nom_source?: string; renomme?: boolean; annotation?: string;
  nom: string; origine_du_nom?: string; nommage?: string;
  noms_dedans?: string[]; multi?: boolean; calque?: string;
  wkt: string; aire_m2: number; aire_declaree_m2?: number | null;
  aire_recalculee_m2?: number | null; aire_verifiee?: boolean;
  perimetre_m?: number | null; incertitudes?: string[];
  pieces?: Piece[]; parois?: Paroi[]; ouvertures?: Ouverture[];
  sous_zones?: SousZone[]; hauteurs_etiquetees?: Hauteur[];
  // Provenance : « zone déclarée » / « reconstruction » (dessiné), « contour
  // inféré » (calculé, à valider), « surface écrite seule » (aucun contour).
  // `anchor` (mètres) pose à l'écran un local sans contour ; la confiance et le
  // rayon arment le bandeau de doute d'un contour inféré.
  source?: string; anchor?: number[] | null;
  confiance_contour?: number | null; rayon_inference_m?: number | null;
}
/** La surcharge locale (optimiste) d'un local après une édition, le temps que
 *  le flux la reserve : nom effectif, annotation, et s'il est renommé. */
interface SurchargeLocale { nom: string; annotation: string; renomme: boolean; }

/** Les valeurs D'AFFICHAGE d'une entité annotable — local OU pièce d'ensemble :
 *  celles du flux, ou la surcharge locale si le métreur vient d'éditer (elle
 *  prime jusqu'au prochain déroulé). Une pièce porte les mêmes champs qu'un
 *  local (nom, nom_source, annotation, renomme), d'où la signature commune. */
function effectif(
    l: { nom?: string | null; nom_source?: string; annotation?: string;
         renomme?: boolean },
    s?: SurchargeLocale):
    { nom: string; annotation: string; renomme: boolean; nomSource: string } {
  const nomSource = l.nom_source ?? l.nom ?? "";
  if (s) return { nom: s.nom, annotation: s.annotation, renomme: s.renomme, nomSource };
  return { nom: l.nom ?? "", annotation: l.annotation ?? "",
           renomme: l.renomme ?? false, nomSource };
}

/** Un libellé de chiffrage UNIQUE et stable. La mission de chiffrage est
 *  identifiée par ce libellé côté serveur : deux entités SANS nom (deux
 *  escaliers, deux locaux anonymes) partageraient sinon la même mission. Le nom
 *  du métreur prime ; à défaut, le type + un suffixe de clé (stable). */
function libelleChiffrage(nom: string, cle?: string,
                          estPiece?: boolean, estEscalier?: boolean): string {
  if (nom) return nom;
  const mot = estEscalier ? "Escalier" : estPiece ? "Pièce" : "Local";
  return cle ? `${mot} ${cle.slice(0, 6)}` : mot;
}

/** L'aire et le périmètre à CHIFFRER. Un contour INFÉRÉ (ou une surface écrite
 *  seule) ne livre PAS son périmètre calculé — géométrie non validée — et se
 *  chiffre sur l'aire ÉCRITE, autorité : « jamais un nombre en silence » vaut
 *  aussi pour la longueur. Un local/pièce/escalier dessiné, lui, chiffre sa
 *  géométrie mesurée. */
function metreDe(o: {
  aire?: number | null; perimetre?: number | null; aireDeclaree?: number | null;
  estInfere?: boolean; estEcritSeule?: boolean;
}): { aire: number | null; perimetre: number | null } {
  if (o.estInfere || o.estEcritSeule) {
    return { aire: o.aireDeclaree ?? o.aire ?? null, perimetre: null };
  }
  return { aire: o.aire ?? null, perimetre: o.perimetre ?? null };
}
interface Chevauchement {
  a: string; b: string; a_rang?: number; b_rang?: number;
  aire_m2: number; wkt?: string;
}
interface Bilan {
  surface_relevee_m2?: number; surface_declaree_m2?: number | null;
  ecart_m2?: number | null; comptes?: Record<string, number>;
  avertissements_non_leves?: number; decisions_connues?: number;
}
type Statut = "attente" | "encours" | "fait" | "echec";
interface Etat {
  statut: Statut; locaux?: number; parois?: number; ouvertures?: number; motif?: string;
}

// ── Icônes de la barre d'actions (traits, à la couleur du bouton) ───────────

const traitSvg = {
  viewBox: "0 0 24 24", fill: "none", stroke: "currentColor",
  strokeWidth: 1.9, strokeLinecap: "round" as const, strokeLinejoin: "round" as const,
};
function IcoAnnoter() {
  return (<svg {...traitSvg}><path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>);
}
function IcoRenommer() {
  return (<svg {...traitSvg}>
    <path d="M20.59 13.41 13.42 20.58a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82Z" />
    <circle cx="7" cy="7" r="1.2" /></svg>);
}
function IcoEchanger() {
  return (<svg {...traitSvg}>
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>);
}
function IcoDetail() {
  return (<svg {...traitSvg}><circle cx="12" cy="12" r="9" />
    <path d="M12 11v5" /><path d="M12 7.6h.01" /></svg>);
}
function IcoChiffrer() {
  return (<svg {...traitSvg}><rect x="5" y="3" width="14" height="18" rx="2" />
    <path d="M8 7h8" />
    <path d="M8.5 12h.01M12 12h.01M15.5 12h.01M8.5 16h.01M12 16h.01M15.5 16h.01" /></svg>);
}
function IcoRetirer() {
  return (<svg {...traitSvg}><path d="M3 6h18" />
    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /></svg>);
}

export function Reconstruction({ affaireId, onEchanger }: {
  affaireId: string;
  // Ouvre l'échange avec l'assistant. Fourni par l'affaire → fenêtre flottante ;
  // absent → repli sur la navigation vers l'onglet Assistant.
  onEchanger?: (amorce: string) => void;
}) {
  const niveaux = useDonnees<{ donnees: NiveauReconstruction[] }>(
    () => api.lire<{ donnees: NiveauReconstruction[] }>(
      `/api/v1/affaires/${affaireId}/reconstruction/niveaux`),
    [affaireId],
  );
  const liste = niveaux.donnees?.donnees ?? [];

  const [etats, poserEtats] = useState<Record<string, Etat>>({});
  const [selection, poserSelection] = useState<string>("");
  const [prep, poserPrep] = useState<string>("");
  const [bornes, poserBornes] = useState<number[]>([0, 0, 100, 100]);
  const [locaux, poserLocaux] = useState<Local[]>([]);
  const [grosOeuvre, poserGrosOeuvre] = useState<MurPlan[]>([]);
  const [reperes, poserReperes] = useState<Repere[]>([]);
  const [etiquettes, poserEtiquettes] = useState<Etiquette[]>([]);
  const [escaliers, poserEscaliers] = useState<Escalier[]>([]);
  const [autresEscaliers, poserAutresEscaliers] = useState<string[]>([]);
  const [chevauchements, poserChevauchements] = useState<Chevauchement[]>([]);
  const [bilan, poserBilan] = useState<Bilan | null>(null);
  const [progres, poserProgres] = useState<{ pas: number; sur: number }>({ pas: 0, sur: 0 });
  const [enMarche, poserEnMarche] = useState(false);
  const source = useRef<EventSource | null>(null);
  const suite = useRef(false);

  useEffect(() => () => source.current?.close(), []);

  function fermer() {
    source.current?.close();
    source.current = null;
  }

  function diffuser(niveau: string, surFin?: (ok: boolean) => void) {
    fermer();
    poserSelection(niveau);
    poserLocaux([]);
    poserGrosOeuvre([]);
    poserReperes([]);
    poserEtiquettes([]);
    poserEscaliers([]);
    poserAutresEscaliers([]);
    poserChevauchements([]);
    poserBilan(null);
    poserProgres({ pas: 0, sur: 0 });
    poserPrep("Lecture du plan…");
    poserEtats((e) => ({ ...e, [niveau]: { statut: "encours" } }));
    let nLocaux = 0, nParois = 0, nOuv = 0;
    const flux = new EventSource(
      `/api/v1/affaires/${affaireId}/reconstruction/niveau`
      + `?niveau=${encodeURIComponent(niveau)}`);
    source.current = flux;
    flux.onmessage = (msg) => {
      const ev = JSON.parse(msg.data);
      if (ev.genre === "preparation") {
        poserPrep(ev.cache ? "Relecture du relevé enrichi…"
          : "Bâti du plan (locaux, parois, ouvertures)…");
      } else if (ev.genre === "cadrage") {
        poserBornes(ev.bornes);
        poserPrep("");
      } else if (ev.genre === "gros_oeuvre") {
        poserGrosOeuvre(ev.murs || []);
      } else if (ev.genre === "reperes") {
        poserReperes(ev.reperes || []);
      } else if (ev.genre === "etiquettes") {
        poserEtiquettes(ev.etiquettes || []);
      } else if (ev.genre === "escaliers") {
        poserEscaliers(ev.escaliers || []);
        poserAutresEscaliers(ev.autres || []);
      } else if (ev.genre === "local") {
        nLocaux += 1;
        nParois += (ev.parois || []).length;
        nOuv += (ev.ouvertures || []).length;
        poserLocaux((ls) => [...ls, ev as Local]);
        if (ev.sur) poserProgres({ pas: ev.pas || 0, sur: ev.sur });
      } else if (ev.genre === "recoupement") {
        poserChevauchements(ev.chevauchements || []);
      } else if (ev.genre === "bilan") {
        poserBilan(ev as Bilan);
      }
    };
    flux.addEventListener("fin", () => {
      fermer();
      poserPrep("");
      poserEtats((e) => ({
        ...e,
        [niveau]: { statut: "fait", locaux: nLocaux, parois: nParois, ouvertures: nOuv },
      }));
      surFin?.(true);
    });
    flux.addEventListener("echec", (msg) => {
      fermer();
      poserPrep("");
      let motif = "La reconstruction s'est interrompue.";
      try { motif = JSON.parse((msg as MessageEvent).data).motif || motif; } catch { /* défaut */ }
      poserEtats((e) => ({ ...e, [niveau]: { statut: "echec", motif } }));
      surFin?.(false);
    });
    flux.onerror = () => {
      if (source.current === flux) {
        fermer();
        poserPrep("");
        poserEtats((e) => ({ ...e, [niveau]: { statut: "echec", motif: "Flux interrompu." } }));
        surFin?.(false);
      }
    };
  }

  function lancerTout() {
    if (!liste.length) return;
    poserEnMarche(true);
    suite.current = true;
    poserEtats(Object.fromEntries(liste.map((n) => [n.niveau, { statut: "attente" as Statut }])));
    const enchainer = (i: number) => {
      if (!suite.current || i >= liste.length) { poserEnMarche(false); return; }
      diffuser(liste[i]!.niveau, () => enchainer(i + 1));
    };
    enchainer(0);
  }

  function arreter() {
    suite.current = false;
    poserEnMarche(false);
    fermer();
  }

  if (niveaux.charge && !niveaux.donnees) {
    return <div className="contenu"><Chargement lignes={3} /></div>;
  }
  if (niveaux.erreur) {
    return <div className="contenu"><Echec erreur={niveaux.erreur} reessayer={niveaux.recharger} /></div>;
  }
  if (!liste.length) {
    return (
      <div className="contenu pile">
        <Avis ton="attention" permanent>
          Aucun niveau à reconstruire. La reconstruction lit les plans d'étage de
          l'instantané actif : préparez puis activez un instantané contenant des
          plans DXF (RDC, R-1, R+1…) dans l'onglet <b>Instantanés</b>.
        </Avis>
      </div>
    );
  }

  const etatSel = selection ? etats[selection] : undefined;

  return (
    <div className="contenu pile">
      <div className="carte">
        <div className="carte__corps pile">
          <div className="rangee rangee--espacee">
            <div className="pile pile--serree" style={{ minWidth: 0 }}>
              <h2>Reconstruction des niveaux</h2>
              <span className="discret">
                Le plan de chaque niveau rebâti en direct — locaux, parois et
                ouvertures — un niveau après l'autre. Survolez, cliquez un local,
                masquez une couche, agrandissez à la molette.
              </span>
            </div>
            {enMarche ? (
              <button type="button" className="bouton bouton--discret" onClick={arreter}>
                Arrêter
              </button>
            ) : (
              <button type="button" className="bouton bouton--primaire" onClick={lancerTout}>
                Lancer la reconstruction
              </button>
            )}
          </div>

          <div className="recon-niveaux">
            {liste.map((n) => {
              const e = etats[n.niveau];
              const actif = selection === n.niveau;
              return (
                <button
                  key={n.niveau}
                  type="button"
                  className={`recon-niveau recon-niveau--${e?.statut ?? "attente"}`
                    + (actif ? " recon-niveau--actif" : "")}
                  onClick={() => diffuser(n.niveau)}
                  title={n.titre}
                >
                  <span className="recon-niveau__puce" aria-hidden="true">
                    {e?.statut === "fait" ? "✓"
                      : e?.statut === "encours" ? "…"
                        : e?.statut === "echec" ? "!" : "○"}
                  </span>
                  <span className="recon-niveau__nom">{n.niveau}</span>
                  {e?.statut === "fait" && (
                    <span className="discret">
                      {e.locaux} loc · {e.parois} parois · {e.ouvertures} ouv.
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          {etatSel?.statut === "echec" && (
            <Avis ton="erreur" permanent>{etatSel.motif}</Avis>
          )}
          {etatSel?.statut === "encours" && progres.sur > 0 && (
            <div className="pile pile--serree">
              <div className="jauge jauge--haute" role="progressbar"
                   aria-valuemin={0} aria-valuemax={progres.sur} aria-valuenow={progres.pas}>
                <div className="jauge__part"
                     style={{ width: `${Math.round(100 * progres.pas / progres.sur)}%` }} />
              </div>
              <span className="discret nombre">Local {progres.pas} / {progres.sur}</span>
            </div>
          )}
        </div>
      </div>

      {selection && (
        <PlanNiveau
          affaireId={affaireId}
          niveau={selection}
          titre={liste.find((n) => n.niveau === selection)?.titre}
          bornes={bornes}
          locaux={locaux}
          grosOeuvre={grosOeuvre}
          reperes={reperes}
          etiquettes={etiquettes}
          escaliers={escaliers}
          autresEscaliers={autresEscaliers}
          chevauchements={chevauchements}
          bilan={bilan}
          prep={prep}
          onEchanger={onEchanger}
        />
      )}
    </div>
  );
}

// ── Les couches affichables (la légende les commande) ───────────────────────

interface Couches {
  contour: boolean; pieces: boolean; murs: boolean; cloisons: boolean;
  portes: boolean; fenetres: boolean; baies: boolean; escaliers: boolean;
  reperes: boolean; textes: boolean;
  annotations: boolean; verification: boolean; chevauchements: boolean;
}
const COUCHES_DEFAUT: Couches = {
  contour: true, pieces: true, murs: true, cloisons: true,
  portes: true, fenetres: true, baies: true, escaliers: true,
  reperes: true, textes: true,
  annotations: true, verification: true, chevauchements: true,
};
/** La légende : un intitulé, une pastille de couleur, et la clé de la couche. */
const LEGENDE: { clef: keyof Couches; libelle: string; classe: string }[] = [
  { clef: "contour", libelle: "Locaux", classe: "leg-contour" },
  { clef: "pieces", libelle: "Pièces", classe: "leg-piece" },
  { clef: "murs", libelle: "Gros œuvre", classe: "leg-mur" },
  { clef: "cloisons", libelle: "Cloisons", classe: "leg-cloison" },
  { clef: "portes", libelle: "Portes", classe: "leg-porte" },
  { clef: "fenetres", libelle: "Fenêtres", classe: "leg-fenetre" },
  { clef: "baies", libelle: "Baies / autres", classe: "leg-baie" },
  { clef: "escaliers", libelle: "Escaliers", classe: "leg-escalier" },
  { clef: "reperes", libelle: "Repères (parvis, rampe…)", classe: "leg-repere" },
  { clef: "textes", libelle: "Textes du plan (survol)", classe: "leg-texte" },
  { clef: "annotations", libelle: "Annotations", classe: "leg-annotation" },
  { clef: "verification", libelle: "Aire non confirmée", classe: "leg-doute" },
  { clef: "chevauchements", libelle: "Chevauchements", classe: "leg-chevauchement" },
];

// ── Le plan d'un niveau : dessin manipulable, légende, détail, bilan ─────────

function PlanNiveau({
  affaireId, niveau, titre, bornes, locaux, grosOeuvre, reperes, etiquettes,
  escaliers, autresEscaliers, chevauchements, bilan, prep, onEchanger,
}: {
  affaireId: string; niveau: string; titre?: string; bornes: number[];
  locaux: Local[]; grosOeuvre: MurPlan[]; reperes: Repere[];
  etiquettes: Etiquette[];
  escaliers: Escalier[]; autresEscaliers: string[];
  chevauchements: Chevauchement[]; bilan: Bilan | null; prep: string;
  onEchanger?: (amorce: string) => void;
}) {
  const [couches, poserCouches] = useState<Couches>(COUCHES_DEFAUT);
  const [actif, poserActif] = useState<number | null>(null);     // rang du local ouvert
  const [survol, poserSurvol] = useState<number | null>(null);   // rang survolé
  const [bulle, poserBulle] = useState<{ x: number; y: number; lignes: string[] } | null>(null);
  // Le chiffrage n'a besoin que du nom, de l'aire et du périmètre — un local
  // comme une pièce d'ensemble les portent. Le détail d'une pièce (tiroir) est
  // visé par sa clé (une pièce n'a pas de rang, contrairement à `actif`).
  const [chiffrer, poserChiffrer] = useState<
    { nom: string; aire: number | null; perimetre: number | null } | null>(null);
  const [detailCle, poserDetailCle] = useState<string | null>(null);
  // Le menu contextuel d'un local (au clic) et l'éditeur qu'il ouvre — ancrés au
  // curseur, en coordonnées d'écran (position: fixed), comme l'infobulle.
  // Menu et éditeur visent une entité par sa CLÉ (local ou pièce d'ensemble) :
  // la clé est l'identité que le back-end annote, et la seule qu'une pièce ait.
  const [menu, poserMenu] = useState<{ cle: string; x: number; y: number } | null>(null);
  const [editeur, poserEditeur] = useState<
    { cle: string; mode: "note" | "nom"; x: number; y: number } | null>(null);
  const [brouillon, poserBrouillon] = useState("");
  const [enreg, poserEnreg] = useState(false);
  const [erreurEdit, poserErreurEdit] = useState("");
  // Les éditions de CETTE session, plaquées par-dessus le flux le temps qu'il le
  // reserve : le marqueur et l'infobulle changent aussitôt, sans re-streamer.
  // Les surcharges optimistes sont clées par la CLÉ géométrique (locaux ET
  // pièces d'ensemble la partagent), non par le rang — un rang ne désigne qu'un
  // local, jamais une pièce.
  const [surcharges, poserSurcharges] = useState<Map<string, SurchargeLocale>>(new Map());
  const svgRef = useRef<SVGSVGElement | null>(null);
  const barreRef = useRef<HTMLDivElement | null>(null);

  // La barre s'ouvre AU-DESSUS du curseur, centrée ; elle bascule dessous si le
  // haut manque, et se replie dans la fenêtre. Mesurée après rendu (largeur
  // variable selon les gestes), corrigée avant peinture — donc sans clignotement.
  useLayoutEffect(() => {
    const el = barreRef.current;
    if (!menu || !el) return;
    const r = el.getBoundingClientRect();
    const w = r.width || 300, h = r.height || 54;
    let left = menu.x - w / 2;
    let top = menu.y - h - 14;
    if (top < 76) top = menu.y + 16;    // pas de place au-dessus : passer dessous
    left = Math.max(12, Math.min(left, window.innerWidth - w - 12));
    top = Math.max(12, Math.min(top, window.innerHeight - h - 12));
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
  }, [menu]);

  // Un défilement, un redimensionnement ou Échap referment barre et éditeur :
  // ils sont ancrés à un point d'écran qui ne vaut plus rien dès que ça bouge.
  useEffect(() => {
    if (!menu && !editeur) return;
    const fermerFlottants = () => { poserMenu(null); poserEditeur(null); };
    const surTouche = (e: KeyboardEvent) => { if (e.key === "Escape") fermerFlottants(); };
    window.addEventListener("scroll", fermerFlottants, true);
    window.addEventListener("resize", fermerFlottants);
    window.addEventListener("keydown", surTouche);
    return () => {
      window.removeEventListener("scroll", fermerFlottants, true);
      window.removeEventListener("resize", fermerFlottants);
      window.removeEventListener("keydown", surTouche);
    };
  }, [menu, editeur]);

  // Une marge autour de l'emprise : sans elle, les parois et les étiquettes des
  // locaux de bord touchent le cadre et se coupent.
  const bwRaw = bornes[2]! - bornes[0]!, bhRaw = bornes[3]! - bornes[1]!;
  const marge = (Math.max(bwRaw, bhRaw) || 1) * 0.03;
  const [bx, by, bw, bh] = [
    bornes[0]! - marge, -bornes[3]! - marge, bwRaw + 2 * marge, bhRaw + 2 * marge,
  ];
  const [vue, poserVue] = useState({ k: 1, cx: bx + bw / 2, cy: by + bh / 2 });

  // Un nouveau cadrage (nouveau niveau) recadre : on ne garde pas le zoom d'avant.
  useEffect(() => {
    poserVue({ k: 1, cx: bx + bw / 2, cy: by + bh / 2 });
    poserActif(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bx, by, bw, bh]);

  const vw = bw / vue.k, vh = bh / vue.k;
  const viewBox = `${vue.cx - vw / 2} ${vue.cy - vh / 2} ${vw} ${vh}`;

  const brutActif = actif != null ? locaux.find((l) => l.rang === actif) ?? null : null;
  // Le tiroir de détail montre AUSSI la surcharge en cours (nom, note, renommage).
  const localActif = brutActif && (() => {
    const e = effectif(brutActif, surcharges.get(brutActif.cle ?? ""));
    return { ...brutActif, nom: e.nom, annotation: e.annotation,
             renomme: e.renomme, nom_source: e.nomSource,
             nommage: e.renomme ? "metreur" : brutActif.nommage };
  })();
  const rangsChevauches = useMemo(() => {
    const s = new Set<number>();
    for (const c of chevauchements) {
      if (c.a_rang != null) s.add(c.a_rang);
      if (c.b_rang != null) s.add(c.b_rang);
    }
    return s;
  }, [chevauchements]);

  // ── Zoom à la molette, autour du pointeur ────────────────────────────────
  // React attache `onWheel` en écouteur PASSIF (React 19) : `preventDefault`
  // y échoue et la page défile. On pose donc l'écouteur nous-mêmes, non passif.
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const onWheel = (ev: WheelEvent) => {
      ev.preventDefault();
      poserMenu(null); poserEditeur(null);   // le zoom déplace le point d'ancrage
      const ctm = svg.getScreenCTM();
      if (!ctm) return;
      const p = new DOMPoint(ev.clientX, ev.clientY).matrixTransform(ctm.inverse());
      poserVue((v) => {
        const k2 = Math.min(40, Math.max(1, v.k * (ev.deltaY < 0 ? 1.15 : 1 / 1.15)));
        if (k2 === 1) return { k: 1, cx: bx + bw / 2, cy: by + bh / 2 };
        const r = v.k / k2;   // homothétie autour du pointeur : il reste fixe
        return { k: k2, cx: p.x + (v.cx - p.x) * r, cy: p.y + (v.cy - p.y) * r };
      });
    };
    svg.addEventListener("wheel", onWheel, { passive: false });
    return () => svg.removeEventListener("wheel", onWheel);
  }, [bx, by, bw, bh]);

  // Glissé = panoramique. On mesure le déplacement en PIXELS d'écran (repère
  // figé au début du geste), converti en unités de plan par l'échelle du départ
  // — pas de rétroaction avec le viewBox qui bouge.
  const glisse = useRef<{ sx: number; sy: number; cx: number; cy: number;
                          s: number; id: number; bouge: boolean } | null>(null);
  function auDebut(ev: React.PointerEvent) {
    if (ev.button !== 0) return;
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const s = Math.min(rect.width / vw, rect.height / vh);   // px d'écran par mètre
    glisse.current = { sx: ev.clientX, sy: ev.clientY, cx: vue.cx, cy: vue.cy,
                       s, id: ev.pointerId, bouge: false };
    // Pas de capture ici : capturer retargeterait le `click` du local vers le
    // svg, et la sélection ne se ferait jamais. On capture au premier vrai glissé.
  }
  function auMouvement(ev: React.PointerEvent) {
    const g = glisse.current;
    if (!g) return;
    const dxs = ev.clientX - g.sx, dys = ev.clientY - g.sy;
    if (!g.bouge && Math.hypot(dxs, dys) < 4) return;        // seuil : clic vs glissé
    if (!g.bouge) {
      g.bouge = true;
      poserMenu(null); poserEditeur(null);   // un panoramique commence : on referme
      (ev.currentTarget as SVGElement).setPointerCapture(g.id);
    }
    poserVue((v) => ({ ...v, cx: g.cx - dxs / g.s, cy: g.cy - dys / g.s }));
  }
  function auRelache(ev: React.PointerEvent) {
    const g = glisse.current;
    if (g?.bouge) (ev.currentTarget as SVGElement).releasePointerCapture?.(g.id);
    // Un vrai glissé ne doit pas valoir sélection : on le retient le temps du clic.
    setTimeout(() => { glisse.current = null; }, 0);
  }

  // Un clic sur un local ouvre le menu contextuel au curseur (pas la sélection
  // directe : le menu porte « voir le détail » parmi ses gestes).
  function ouvrirMenu(cle: string, ev: React.MouseEvent) {
    if (glisse.current?.bouge) return;   // c'était un panoramique, pas un clic
    if (!cle) return;                    // entité sans clé : rien à annoter
    poserBulle(null);
    poserEditeur(null);
    poserErreurEdit("");
    poserMenu({ cle, x: ev.clientX, y: ev.clientY });
  }

  function poserBulleDe(ev: React.MouseEvent, lignes: string[]) {
    poserBulle({ x: ev.clientX, y: ev.clientY, lignes });
  }

  // ── Les gestes du menu : annoter, renommer, échanger, détailler, chiffrer ──
  function ouvrirEditeur(cle: string, mode: "note" | "nom", x: number, y: number) {
    const d = parCle.get(cle);
    poserBrouillon(mode === "note" ? (d?.annotation ?? "") : (d?.nom ?? ""));
    poserErreurEdit("");
    poserMenu(null);
    // Ancré près du curseur, mais toujours dans la fenêtre.
    const w = 320, h = mode === "note" ? 220 : 150;
    const left = Math.max(12, Math.min(x - 24, window.innerWidth - w - 12));
    const top = Math.max(12, Math.min(y, window.innerHeight - h - 12));
    poserEditeur({ cle, mode, x: left, y: top });
  }

  async function enregistrer() {
    if (!editeur) return;
    const d = parCle.get(editeur.cle);
    if (!d || !d.cle) {
      poserErreurEdit(
        (d?.estEscalier ? "Cet escalier" : d?.estPiece ? "Cette pièce" : "Ce local")
        + " n'a pas de clé — relancez la reconstruction du niveau.");
      return;
    }
    const valeur = brouillon.trim();
    poserEnreg(true);
    poserErreurEdit("");
    const corps: Record<string, unknown> = {
      niveau, cle: d.cle, apercu: d.nomSource || d.nom,
    };
    // Le rang n'est qu'un repère de réparation, et ne vaut que pour un local :
    // une pièce n'a pas de rang propre, on le laisse donc à défaut (null).
    if (!d.estPiece && !d.estEscalier && d.rang != null) corps.rang = d.rang;
    if (editeur.mode === "note") corps.note = valeur;
    else corps.nom = valeur;
    try {
      await api.remplacer(
        `/api/v1/affaires/${affaireId}/reconstruction/annotations`, corps);
      // Optimiste : le marqueur et l'infobulle changent tout de suite.
      poserSurcharges((m) => {
        const n = new Map(m);
        const base: SurchargeLocale = n.get(d.cle)
          ?? { nom: d.nom, annotation: d.annotation, renomme: d.renomme };
        if (editeur.mode === "note") {
          n.set(d.cle, { ...base, annotation: valeur });
        } else {
          n.set(d.cle, { ...base, nom: valeur || d.nomSource, renomme: !!valeur });
        }
        return n;
      });
      poserEditeur(null);
    } catch (exc) {
      poserErreurEdit(exc instanceof ErreurApi ? exc.message
        : "L'enregistrement a échoué — réessayez.");
    } finally {
      poserEnreg(false);
    }
  }

  async function retirer(cle: string) {
    const d = parCle.get(cle);
    if (!d || !d.cle) return;
    poserMenu(null);
    try {
      await api.retirer(
        `/api/v1/affaires/${affaireId}/reconstruction/annotations`
        + `?niveau=${encodeURIComponent(niveau)}&cle=${encodeURIComponent(d.cle)}`);
      poserSurcharges((m) => {
        const n = new Map(m);
        n.set(d.cle, { nom: d.nomSource, annotation: "", renomme: false });
        return n;
      });
    } catch { /* la prochaine relecture rétablira la vérité */ }
  }

  // Échanger avec les agents : un message pré-rempli citant l'entité (l'assistant
  // est déjà cadré sur l'affaire et l'instantané). Ouvre la fenêtre flottante
  // quand l'affaire la fournit ; sinon repli sur l'onglet Assistant.
  function echanger(cle: string) {
    const d = parCle.get(cle);
    poserMenu(null);
    const quoi = d?.estEscalier ? "l'escalier"
      : d?.estRepere ? (d.estRampe ? "la rampe" : "l'espace")
      : d?.estPiece ? "la pièce" : "le local";
    const nom = d?.nom || d?.nomSource || "ce local";
    // Escalade « agent » (P5) : pour un local SANS contour (ou à contour inféré,
    // à valider), l'échange devient une TÂCHE topologique explicite — l'agent
    // dispose de Resolve_Room_Boundary et peut rendre un WKT. Sinon, ouverture
    // conversationnelle ordinaire.
    let amorce: string;
    if (d?.estEcritSeule) {
      const s = d.aireDeclaree != null ? `${d.aireDeclaree} m²` : "surface écrite";
      amorce = `Le local « ${nom} » (niveau ${niveau}) n'a AUCUN contour dessiné `
        + `au plan général — seulement sa surface écrite (${s}). Peux-tu établir `
        + `son contour ? Referme le vide autour de son étiquette `
        + `(Resolve_Room_Boundary), regarde aussi les planches de détail, vérifie `
        + `l'aire contre la surface écrite, et rends le WKT du contour. `;
    } else if (d?.estInfere) {
      amorce = `Le contour ${quoi} « ${nom} » (niveau ${niveau}) est INFÉRÉ (aucune `
        + `zone dessinée) et reste À VALIDER. Peux-tu le vérifier — voire le `
        + `corriger — et rendre le WKT du contour retenu ? `;
    } else if (d?.estRepere) {
      amorce = `${d.estRampe ? "La rampe" : "L'espace"} « ${nom} » (niveau `
        + `${niveau}) est nommé au plan mais SANS contour ni surface dessinés `
        + `(un simple repère de texte). Peux-tu établir son emprise — referme le `
        + `vide autour de son étiquette (Resolve_Room_Boundary), en t'appuyant sur `
        + `le gros œuvre alentour — et rendre le WKT ${d.estRampe ? "et la pente "
        : ""}? `;
    } else {
      amorce = `À propos de ${quoi} « ${nom} » (niveau ${niveau}) : `;
    }
    if (onEchanger) { onEchanger(amorce); return; }
    naviguer(`/affaires/${affaireId}?onglet=assistant`
      + `&amorce=${encodeURIComponent(amorce)}`);
  }

  const dessins = useMemo(() => locaux.map((l) => {
    const parois = l.parois || [];
    const tracer = (gard: (t?: string) => boolean) =>
      cheminLigne(parois.filter((p) => gard(p.type)).flatMap((p) => anneaux(p.wkt)));
    // Les arêtes du CONTOUR du local : c'est là que vivent les ouvertures. On y
    // aligne ET colle chaque ouverture (le contour est un polygone net, à la
    // différence des corps de mur épais qui la faisaient flotter à côté).
    const bords = aretes(l.wkt);
    const e = effectif(l, surcharges.get(l.cle ?? ""));
    const marque = !!(e.annotation || e.renomme);
    // Provenance : contour INFÉRÉ (calculé, à valider) ou SURFACE ÉCRITE SEULE
    // (aucun contour — posé à son ancre). Distincts du dessiné, jamais confondus.
    const estInfere = l.source === "contour inféré";
    const estEcritSeule = l.source === "surface écrite seule";
    const ancre: [number, number] | null =
      (l.anchor && l.anchor.length === 2)
        ? [l.anchor[0]!, -l.anchor[1]!] : null;
    // Chaque PIÈCE d'un ensemble devient une entité annotable à part : sa clé,
    // son nom effectif (surcharge comprise), son tracé cliquable et, s'il est
    // annoté, son marqueur. Sans clé (contour illisible) elle reste décorative.
    const pieceList = (l.pieces || [])
      .filter((p) => (p.wkt || "").trim())
      .map((p) => {
        const pe = effectif(p, p.cle ? surcharges.get(p.cle) : undefined);
        const pmarque = !!(pe.annotation || pe.renomme);
        return {
          cle: p.cle || "", nom: pe.nom, nomSource: pe.nomSource,
          annotation: pe.annotation, renomme: pe.renomme, marque: pmarque,
          chemin: cheminPoly(anneaux(p.wkt || "")), wkt: p.wkt || "",
          centre: pmarque ? centreEcran(p.wkt || "") : null,
          aire: p.aire_m2 ?? null, perimetre: p.perimetre_m ?? null,
          cloisonManquante: !!p.cloison_manquante, noms: p.noms || [],
        };
      });
    return {
      rang: l.rang, cle: l.cle, nom: e.nom, nomSource: e.nomSource,
      annotation: e.annotation, renomme: e.renomme, marque,
      estInfere, estEcritSeule, ancre,
      aireDeclaree: l.aire_declaree_m2 ?? null,
      // Le point de repère du marqueur : le centroïde du contour, ou l'ancre à
      // défaut (une surface écrite seule n'a pas de contour).
      centre: marque ? (centreEcran(l.wkt) ?? ancre) : null,
      aire: l.aire_m2, perimetre: l.perimetre_m ?? null,
      verifie: l.aire_verifiee !== false,
      // L'infobulle du local, prête à reposer quand le curseur QUITTE une de ses
      // pièces (sinon elle ne reviendrait pas : le survol du groupe ne se
      // redéclenche pas sans en sortir d'abord). Elle dit la provenance du
      // contour — un contour inféré ou une surface écrite seule est « à valider ».
      bulle: [
        e.nom || (estEcritSeule ? "Local sans contour" : "Local sans nom"),
        estEcritSeule
          ? `Surface écrite ${fmt(l.aire_declaree_m2 ?? l.aire_m2)} m² `
            + "— contour à établir"
          : `Aire ${fmt(l.aire_m2)} m²`
            + (estInfere ? " — contour inféré, à valider" : ""),
        ...(estInfere && l.aire_declaree_m2 != null
          ? [`Écrit au plan ${fmt(l.aire_declaree_m2)} m²`
             + (l.confiance_contour != null
                ? ` · confiance ${Math.round(l.confiance_contour * 100)} %` : "")]
          : []),
        ...(!estInfere && !estEcritSeule
          ? [l.aire_verifiee !== false ? "Aire confirmée par la géométrie"
             : "Aire NON confirmée — à vérifier"] : []),
        ...(e.annotation ? [`✎ ${e.annotation}`] : []),
      ],
      piece: cheminPoly(anneaux(l.wkt)),
      pieceList,
      murs: tracer((t) => t !== "cloison"),
      cloisons: tracer((t) => t === "cloison"),
      ouvertures: (l.ouvertures || [])
        .filter((o) => o.position_m && o.position_m.length === 2)
        .map((o) => {
          const ox = o.position_m![0]!, oy = -o.position_m![1]!;
          // Le trait suit la largeur réelle mais BORNÉ à 2,5 m : une baie de
          // 5,78 m tracée entière déborderait sa paroi ; la vraie largeur reste
          // en infobulle. On COLLE l'ouverture au point projeté sur le contour ;
          // trop loin (> 1,2 m) on ne colle pas et on la marque en point.
          const larg = Math.max(0.2, Math.min(o.largeur_m || 0.7, 2.5));
          const { angle, dist, px, py } = orienter(bords, ox, oy);
          const surParoi = dist <= 1.2;
          return {
            // `ox,oy` (position BRUTE) sert au dédoublonnage — il coïncide pour
            // une porte mitoyenne vue des deux locaux ; `x,y` (collé au contour)
            // sert au tracé, et diffère d'un local à l'autre (faces intérieures).
            ox, oy, x: surParoi ? px : ox, y: surParoi ? py : oy,
            larg, angle, surParoi, genre: o.genre,
            lignes: [
              libelleOuverture(o.genre),
              o.largeur_m ? `Largeur ${fmt(o.largeur_m)} m` : "",
              o.hauteur_m ? `Hauteur ${fmt(o.hauteur_m)} m` : "",
            ].filter(Boolean),
          };
        }),
    };
  }), [locaux, surcharges]);

  // Les REPÈRES d'espace (parvis, rampe, circulation sans local) : chacun un nom
  // posé à son ancre, ANNOTABLE comme un escalier. Nom d'affichage = le renommage
  // du métreur, sinon le texte du plan. Repère écran (Y descendant).
  const reperesDessin = useMemo(() => reperes.map((r) => {
    const e = effectif({ nom: r.nom, nom_source: r.nom_source,
      annotation: r.annotation, renomme: r.renomme },
      r.cle ? surcharges.get(r.cle) : undefined);
    const marque = !!(e.annotation || e.renomme);
    const ancre: [number, number] | null =
      (r.anchor && r.anchor.length === 2) ? [r.anchor[0]!, -r.anchor[1]!] : null;
    const label = (e.renomme && e.nom) ? e.nom : (r.texte || e.nomSource || "");
    return {
      cle: r.cle || "", nom: e.nom, nomSource: e.nomSource,
      annotation: e.annotation, renomme: e.renomme, marque,
      label, famille: r.famille || "", estRampe: !!r.est_rampe, ancre,
      hauteurs: r.hauteurs || [],
    };
  }), [reperes, surcharges]);

  // Le gros œuvre du PLAN ENTIER : tous les corps de mur/cloison réunis en deux
  // chemins (mur / cloison). Couche de BASE, sous les locaux et non interactive —
  // elle fait apparaître la structure là où AUCUN local n'est détecté (le parvis,
  // les cages, la rampe). Les parois PAR LOCAL (survolables, chiffrées) restent
  // dessinées par-dessus : cette couche ne les remplace pas, elle comble le
  // hors-local que `get_walls_for_room` ne pouvait rendre.
  const grosOeuvreDessin = useMemo(() => ({
    mur: cheminLigne(grosOeuvre.filter((m) => m.type !== "cloison")
      .flatMap((m) => anneaux(m.wkt))),
    cloison: cheminLigne(grosOeuvre.filter((m) => m.type === "cloison")
      .flatMap((m) => anneaux(m.wkt))),
  }), [grosOeuvre]);

  // L'accès par CLÉ, pour que le menu et l'éditeur lisent les valeurs courantes
  // d'une entité — local OU pièce d'ensemble. `estPiece` distingue les gestes
  // propres au local (détail, chiffrage) ; `rang` sert de repère de réparation.
  // Les escaliers, chacun projeté comme une entité annotable : nom effectif
  // (surcharge comprise), tracé (les traits), zone cliquable (l'emprise), repère
  // du marqueur, aire/périmètre/traits pour le détail et le chiffrage.
  const escaliersDessin = useMemo(() => escaliers.map((s) => {
    const e = effectif({ nom: s.nom, nom_source: s.nom_source,
      annotation: s.annotation, renomme: s.renomme },
      s.cle ? surcharges.get(s.cle) : undefined);
    const marque = !!(e.annotation || e.renomme);
    return {
      cle: s.cle || "", nom: e.nom, nomSource: e.nomSource,
      annotation: e.annotation, renomme: e.renomme, marque,
      chemin: (s.traces || []).map((w) => cheminLigne(anneaux(w))).join(" "),
      centre: (marque && s.wkt) ? centreEcran(s.wkt) : null,
      aire: s.aire_m2 ?? null, perimetre: s.perimetre_m ?? null,
      traits: s.traits ?? null, wkt: s.wkt || "",
    };
  }), [escaliers, surcharges]);

  // `aire`/`perimetre` (et, pour une pièce, `wkt`/`cloisonManquante`/`noms` ;
  // pour un escalier, `traits`) arment le chiffrage et le tiroir de détail —
  // local, pièce d'ensemble et escalier sont tous détaillables et chiffrables.
  interface EntiteAnnotable {
    cle: string; nom: string; nomSource: string; annotation: string;
    renomme: boolean; marque: boolean; estPiece: boolean; estEscalier: boolean;
    rang: number | null; aire: number | null; perimetre: number | null;
    wkt?: string; cloisonManquante?: boolean; noms?: string[];
    traits?: number | null;
    // Provenance d'un local : un contour inféré (ou une surface écrite seule)
    // ne chiffre PAS son périmètre calculé, et se chiffre sur l'aire ÉCRITE.
    estInfere?: boolean; estEcritSeule?: boolean; aireDeclaree?: number | null;
    // Un REPÈRE d'espace (parvis, rampe…) : annotable, mais sans géométrie —
    // ni aire ni périmètre à chiffrer (« géométrie à établir »).
    estRepere?: boolean; famille?: string; estRampe?: boolean;
    hauteurs?: Hauteur[];
  }
  const parCle = useMemo(() => {
    const m = new Map<string, EntiteAnnotable>();
    for (const d of dessins) {
      if (d.cle) m.set(d.cle, {
        cle: d.cle, nom: d.nom, nomSource: d.nomSource, annotation: d.annotation,
        renomme: d.renomme, marque: d.marque, estPiece: false, estEscalier: false,
        rang: d.rang, aire: d.aire ?? null, perimetre: d.perimetre ?? null,
        estInfere: d.estInfere, estEcritSeule: d.estEcritSeule,
        aireDeclaree: d.aireDeclaree });
      for (const p of d.pieceList) if (p.cle) m.set(p.cle, {
        cle: p.cle, nom: p.nom, nomSource: p.nomSource, annotation: p.annotation,
        renomme: p.renomme, marque: p.marque, estPiece: true, estEscalier: false,
        rang: null, aire: p.aire, perimetre: p.perimetre, wkt: p.wkt,
        cloisonManquante: p.cloisonManquante, noms: p.noms });
    }
    // Les escaliers en dernier, sans écraser un local/pièce de même clé (cas nul
    // en pratique — géométries distinctes — mais on ne clobbere jamais).
    for (const s of escaliersDessin) if (s.cle && !m.has(s.cle)) m.set(s.cle, {
      cle: s.cle, nom: s.nom, nomSource: s.nomSource, annotation: s.annotation,
      renomme: s.renomme, marque: s.marque, estPiece: false, estEscalier: true,
      rang: null, aire: s.aire, perimetre: s.perimetre, wkt: s.wkt,
      traits: s.traits });
    // Les repères d'espace : annotables, sans aire ni périmètre — le chiffrage
    // restera muet (pas de nombre fabriqué). Ne clobbère jamais une entité dessinée.
    for (const r of reperesDessin) if (r.cle && !m.has(r.cle)) m.set(r.cle, {
      cle: r.cle, nom: r.nom, nomSource: r.nomSource, annotation: r.annotation,
      renomme: r.renomme, marque: r.marque, estPiece: false, estEscalier: false,
      rang: null, aire: null, perimetre: null,
      estRepere: true, famille: r.famille, estRampe: r.estRampe,
      hauteurs: r.hauteurs });
    return m;
  }, [dessins, escaliersDessin, reperesDessin]);

  // Les tracés d'escalier NON rattachés à un escalier (flèches, fragments) :
  // réunis en un chemin décoratif, non interactif.
  const traceAutres = useMemo(
    () => autresEscaliers.map((w) => cheminLigne(anneaux(w))).join(" "),
    [autresEscaliers]);

  // Les ouvertures, DÉDOUBLONNÉES et tracées UNE fois (une porte sur une paroi
  // mitoyenne est relevée par les DEUX locaux voisins ; sans cela, elle se
  // dessine en double — les grappes de ronds qu'on voyait). Grille ~12 cm.
  const ouvertures = useMemo(() => {
    const vus = new Set<string>();
    const out: (typeof dessins)[number]["ouvertures"] = [];
    for (const d of dessins) {
      for (const o of d.ouvertures) {
        const cle = `${Math.round(o.ox * 8)}:${Math.round(o.oy * 8)}`;
        if (vus.has(cle)) continue;
        vus.add(cle);
        out.push(o);
      }
    }
    return out;
  }, [dessins]);

  const totalOuv = ouvertures.length;
  const totalParois = locaux.reduce((s, l) => s + (l.parois || []).length, 0);

  return (
    <div className="carte">
      <div className="carte__corps pile pile--serree">
        <div className="rangee rangee--espacee">
          <h3 style={{ margin: 0 }}>{niveau}</h3>
          <div className="rangee" style={{ gap: "var(--mo-space-2)" }}>
            <span className="discret tronque" title={titre}
                  style={{ maxWidth: "28ch" }}>{titre}</span>
            <button type="button" className="bouton bouton--discret"
                    disabled={!locaux.length}
                    onClick={() => svgRef.current && exporter(svgRef.current, niveau, "svg")}>
              SVG
            </button>
            <button type="button" className="bouton bouton--discret"
                    disabled={!locaux.length}
                    onClick={() => svgRef.current && exporter(svgRef.current, niveau, "png")}>
              PNG
            </button>
          </div>
        </div>

        {prep && <Avis ton="info" permanent>{prep}</Avis>}

        <Legende couches={couches} basculer={(k) =>
          poserCouches((c) => ({ ...c, [k]: !c[k] }))} />

        <div className="recon-plan defilant recon-plan--interactif">
          <svg ref={svgRef} viewBox={viewBox} className="recon-svg"
               preserveAspectRatio="xMidYMid meet" role="img"
               aria-label={`Plan reconstruit du niveau ${niveau}`}
               onPointerDown={auDebut} onPointerMove={auMouvement}
               onPointerUp={auRelache} onPointerLeave={() => poserBulle(null)}
               style={{ cursor: glisse.current?.bouge ? "grabbing" : "grab" }}>
            {/* Le gros œuvre du plan entier — couche de BASE, sous les locaux et
                non interactive (elle ne vole aucun clic). Elle comble la
                structure hors des locaux détectés : parvis, cages, rampe. */}
            {(grosOeuvreDessin.mur || grosOeuvreDessin.cloison) && (
              <g className="recon-gros-oeuvre" style={{ pointerEvents: "none" }}>
                {couches.cloisons && grosOeuvreDessin.cloison && (
                  <path d={grosOeuvreDessin.cloison}
                        className="recon-paroi recon-paroi--cloison"
                        vectorEffect="non-scaling-stroke" />
                )}
                {couches.murs && grosOeuvreDessin.mur && (
                  <path d={grosOeuvreDessin.mur} className="recon-paroi"
                        vectorEffect="non-scaling-stroke" />
                )}
              </g>
            )}
            {dessins.map((d) => {
              const estActif = d.rang === actif;
              const estSurvol = d.rang === survol;
              const douteux = couches.verification && !d.verifie;
              return (
                <g key={d.rang}
                   className={"recon-local"
                     + (estActif ? " recon-local--actif" : "")
                     + (estSurvol ? " recon-local--survol" : "")
                     + (d.cle && d.cle === menu?.cle ? " recon-local--vise" : "")}
                   onClick={(e) => ouvrirMenu(d.cle ?? "", e)}
                   onMouseEnter={(e) => { poserSurvol(d.rang); poserBulleDe(e, d.bulle); }}
                   onMouseMove={(e) => poserBulle((b) => b ? { ...b, x: e.clientX, y: e.clientY } : b)}
                   onMouseLeave={() => { poserSurvol(null); poserBulle(null); }}
                   style={{ cursor: "pointer" }}>
                  {couches.contour && d.piece && (
                    <path d={d.piece}
                          className={"recon-local__contour"
                            + (douteux ? " recon-local__contour--doute" : "")
                            + (d.estInfere ? " recon-local__contour--infere" : "")}
                          vectorEffect="non-scaling-stroke" />
                  )}
                  {/* Surface écrite seule : aucun contour — un repère cliquable à
                      l'ancre de l'étiquette, « contour à établir ». */}
                  {couches.contour && d.estEcritSeule && d.ancre && (
                    <circle cx={d.ancre[0]} cy={d.ancre[1]} r={vw * 0.013}
                            className="recon-local__ancre"
                            vectorEffect="non-scaling-stroke" />
                  )}
                  {couches.pieces && d.pieceList.map((p, pi) => (
                    <path key={`p${pi}`} d={p.chemin}
                          className={"recon-local__piece"
                            + (p.cle ? " recon-local__piece--annotable" : "")}
                          vectorEffect="non-scaling-stroke"
                          style={p.cle ? { cursor: "pointer" } : undefined}
                          onClick={p.cle ? (ev) => {
                            ev.stopPropagation();   // ne pas viser aussi l'ensemble
                            ouvrirMenu(p.cle, ev);
                          } : undefined}
                          onMouseEnter={(ev) => poserBulleDe(ev, [
                            p.nom || (p.noms.length ? p.noms.join(" / ")
                              : "Pièce sans nom"),
                            p.aire != null ? `Aire ${fmt(p.aire)} m²` : "",
                            p.cloisonManquante
                              ? "Cloison manquante — plusieurs locaux confondus" : "",
                            ...(p.annotation ? [`✎ ${p.annotation}`] : []),
                          ].filter(Boolean))}
                          onMouseMove={(ev) => poserBulle((b) =>
                            b ? { ...b, x: ev.clientX, y: ev.clientY } : b)}
                          onMouseLeave={(ev) => poserBulleDe(ev, d.bulle)} />
                  ))}
                  {couches.cloisons && d.cloisons && (
                    <path d={d.cloisons} className="recon-paroi recon-paroi--cloison"
                          vectorEffect="non-scaling-stroke" />
                  )}
                  {couches.murs && d.murs && (
                    <path d={d.murs} className="recon-paroi"
                          vectorEffect="non-scaling-stroke" />
                  )}
                  {couches.annotations && d.marque && d.centre && (
                    <circle cx={d.centre[0]} cy={d.centre[1]} r={vw * 0.009}
                            className={"recon-marque"
                              + (d.renomme ? " recon-marque--nom" : "")}
                            vectorEffect="non-scaling-stroke" />
                  )}
                  {couches.annotations && d.pieceList.map((p, pi) =>
                    p.marque && p.centre ? (
                      <circle key={`pm${pi}`} cx={p.centre[0]} cy={p.centre[1]}
                              r={vw * 0.008}
                              className={"recon-marque"
                                + (p.renomme ? " recon-marque--nom" : "")}
                              vectorEffect="non-scaling-stroke" />
                    ) : null)}
                </g>
              );
            })}

            {/* TOUS les textes du plan, révélés au SURVOL : une pastille discrète
                à chaque étiquette (nom, hauteur, finition, note, symbole) ; le
                texte et son calque s'affichent au passage. De l'affichage pur —
                aucun clic, aucun métré. Les escaliers/repères, dessinés au-dessus,
                gardent leur propre infobulle (plus riche) à leur emplacement. */}
            {couches.textes && (
              <g className="recon-textes">
                {etiquettes.map((t, i) => (t.anchor && t.anchor.length === 2) ? (
                  <circle key={`et${i}`} cx={t.anchor[0]!} cy={-t.anchor[1]!}
                          r={vw * 0.0045} className="recon-texte__pt"
                          vectorEffect="non-scaling-stroke"
                          onMouseEnter={(e) => poserBulleDe(e, [
                            t.texte || "",
                            t.calque ? `calque ${t.calque}` : "",
                          ].filter(Boolean))}
                          onMouseMove={(e) => poserBulle((b) =>
                            b ? { ...b, x: e.clientX, y: e.clientY } : b)}
                          onMouseLeave={() => poserBulle(null)} />
                ) : null)}
              </g>
            )}

            {couches.escaliers && (
              <>
                {escaliersDessin.map((s, i) => (
                  <g key={`esc${i}`} className="recon-escalier-grp"
                     onClick={(e) => { if (s.cle) ouvrirMenu(s.cle, e); }}
                     onMouseEnter={(e) => poserBulleDe(e, [
                       s.nom || "Escalier",
                       s.aire != null ? `Emprise ${fmt(s.aire)} m²` : "",
                       s.traits != null ? `${s.traits} traits` : "",
                       ...(s.annotation ? [`✎ ${s.annotation}`] : []),
                     ].filter(Boolean))}
                     onMouseMove={(e) => poserBulle((b) =>
                       b ? { ...b, x: e.clientX, y: e.clientY } : b)}
                     onMouseLeave={() => poserBulle(null)}
                     style={s.cle ? { cursor: "pointer" } : undefined}>
                    {/* La cible du clic SUIT les traits (un trait épais mais
                        invisible), et non l'enveloppe convexe : sinon la zone
                        transparente d'un escalier en L masquerait le local logé
                        dans le creux de son emprise. */}
                    {s.chemin && s.cle && (
                      <path d={s.chemin} className="recon-escalier__hit"
                            vectorEffect="non-scaling-stroke" />
                    )}
                    {s.chemin && (
                      <path d={s.chemin} className="recon-escalier"
                            vectorEffect="non-scaling-stroke" />
                    )}
                    {couches.annotations && s.marque && s.centre && (
                      <circle cx={s.centre[0]} cy={s.centre[1]} r={vw * 0.008}
                              className={"recon-marque"
                                + (s.renomme ? " recon-marque--nom" : "")}
                              vectorEffect="non-scaling-stroke" />
                    )}
                  </g>
                ))}
                {traceAutres && (
                  <path d={traceAutres} className="recon-escalier"
                        vectorEffect="non-scaling-stroke" />
                )}
              </>
            )}

            {/* Les REPÈRES d'espace : un nom posé au plan (parvis, rampe…) sans
                local. Le texte est POSÉ à son ancre, avec une pastille cliquable
                — même menu qu'un escalier (annoter / nommer / échanger / détail /
                chiffrer). Le survol dit le texte, la famille et les hauteurs. */}
            {couches.reperes && reperesDessin.map((r, i) => r.ancre ? (
              <g key={`rep${i}`} className="recon-repere-grp"
                 onClick={(e) => { if (r.cle) ouvrirMenu(r.cle, e); }}
                 onMouseEnter={(e) => poserBulleDe(e, [
                   r.label || "Repère",
                   r.estRampe ? "Rampe — géométrie à établir"
                     : (r.famille ? `Espace (${r.famille}) — géométrie à établir`
                        : "Espace nommé — géométrie à établir"),
                   ...r.hauteurs.map((h) => `${h.genre || "h"} ${fmt(h.valeur_m)} m`),
                   ...(r.annotation ? [`✎ ${r.annotation}`] : []),
                 ].filter(Boolean))}
                 onMouseMove={(e) => poserBulle((b) =>
                   b ? { ...b, x: e.clientX, y: e.clientY } : b)}
                 onMouseLeave={() => poserBulle(null)}
                 style={r.cle ? { cursor: "pointer" } : undefined}>
                {/* Une pastille cliquable au point de l'étiquette. Le NOM se
                    lit au survol (comme tous les textes du plan) ; annoté, il
                    reste posé pour signaler la note. */}
                <circle cx={r.ancre[0]} cy={r.ancre[1]} r={vw * 0.013}
                        className={"recon-repere__pt"
                          + (r.estRampe ? " recon-repere__pt--rampe" : "")
                          + (r.cle && r.cle === menu?.cle ? " recon-repere__pt--vise" : "")}
                        vectorEffect="non-scaling-stroke" />
                {r.marque && (
                  <text x={r.ancre[0] + vw * 0.02} y={r.ancre[1] + vw * 0.006}
                        className="recon-repere__txt"
                        style={{ fontSize: `${vw * 0.026}px` }}>
                    {r.label}
                  </text>
                )}
                {couches.annotations && r.marque && (
                  <circle cx={r.ancre[0]} cy={r.ancre[1]} r={vw * 0.008}
                          className={"recon-marque"
                            + (r.renomme ? " recon-marque--nom" : "")}
                          vectorEffect="non-scaling-stroke" />
                )}
              </g>
            ) : null)}

            {/* Les ouvertures, une couche À PART et dédoublonnée : chaque porte /
                fenêtre est tracée UNE fois, alignée sur sa paroi (segment de sa
                largeur réelle), ou en point si aucune paroi n'est proche. */}
            {ouvertures.map((o, i) => {
              const genre = o.genre || "baie";
              const visible = genre === "porte" ? couches.portes
                : genre === "fenetre" ? couches.fenetres : couches.baies;
              if (!visible) return null;
              const survolO = (e: React.MouseEvent) => poserBulleDe(e, o.lignes);
              const suivi = (e: React.MouseEvent) =>
                poserBulle((b) => b ? { ...b, x: e.clientX, y: e.clientY } : b);
              const quitter = () => poserBulle(null);
              if (!o.surParoi) {
                return (
                  <circle key={i} cx={o.x} cy={o.y} r={0.22}
                          className={`recon-ouverture recon-ouverture--${genre} recon-ouverture--point`}
                          vectorEffect="non-scaling-stroke"
                          onMouseEnter={survolO} onMouseMove={suivi} onMouseLeave={quitter} />
                );
              }
              const hx = (o.larg / 2) * Math.cos(o.angle);
              const hy = (o.larg / 2) * Math.sin(o.angle);
              return (
                <line key={i} x1={o.x - hx} y1={o.y - hy} x2={o.x + hx} y2={o.y + hy}
                      className={`recon-ouverture recon-ouverture--${genre}`}
                      vectorEffect="non-scaling-stroke"
                      onMouseEnter={survolO} onMouseMove={suivi} onMouseLeave={quitter} />
              );
            })}

            {couches.chevauchements && chevauchements.map((c, i) => (
              <path key={i} d={cheminPoly(anneaux(c.wkt || ""))}
                    className="recon-chevauchement" vectorEffect="non-scaling-stroke"
                    onMouseEnter={(e) => poserBulleDe(e,
                      [`Chevauchement : « ${c.a} » et « ${c.b} »`, `${fmt(c.aire_m2)} m² disputés`])}
                    onMouseMove={(e) => poserBulle((b) => b ? { ...b, x: e.clientX, y: e.clientY } : b)}
                    onMouseLeave={() => poserBulle(null)} />
            ))}
          </svg>

          {vue.k > 1 && (
            <button type="button" className="recon-recadrer bouton bouton--discret"
                    onClick={() => poserVue({ k: 1, cx: bx + bw / 2, cy: by + bh / 2 })}>
              Recadrer
            </button>
          )}
        </div>

        <div className="rangee rangee--espacee">
          <span className="discret">
            {locaux.length} {locaux.length > 1 ? "locaux" : "local"} · {totalParois} parois
            {" · "}{totalOuv} ouvertures
            {rangsChevauches.size > 0 && (
              <span className="recon-alerte"> · {chevauchements.length} chevauchement
                {chevauchements.length > 1 ? "s" : ""}</span>
            )}
          </span>
        </div>

        {bilan && <BilanNiveau bilan={bilan} />}
      </div>

      {bulle && (
        <div className="recon-bulle" style={{ left: bulle.x + 14, top: bulle.y + 14 }}
             role="tooltip">
          {bulle.lignes.map((t, i) => (
            <div key={i} className={i === 0 ? "recon-bulle__titre" : "discret"}>{t}</div>
          ))}
        </div>
      )}

      {menu && (() => {
        const d = parCle.get(menu.cle);
        if (!d) return null;
        return (
          <>
            <div className="recon-barre-fond" onClick={() => poserMenu(null)} />
            <div className="recon-barre" role="toolbar" ref={barreRef}
                 aria-label={`Actions de « ${d.nom || "sans nom"} »`}
                 style={{ left: menu.x, top: menu.y }}>
              <button type="button" className="recon-outil"
                      onClick={() => ouvrirEditeur(menu.cle, "note", menu.x, menu.y)}>
                <span className="recon-outil__bulle">
                  {d.annotation ? "Modifier l'annotation" : "Annoter"}</span>
                <IcoAnnoter />
              </button>
              <button type="button" className="recon-outil"
                      onClick={() => ouvrirEditeur(menu.cle, "nom", menu.x, menu.y)}>
                <span className="recon-outil__bulle">
                  {d.estEscalier ? "Nommer l'escalier"
                    : d.estRepere ? "Nommer l'espace"
                    : d.estPiece ? "Nommer la pièce" : "Renommer l'étiquette"}</span>
                <IcoRenommer />
              </button>
              <button type="button" className="recon-outil"
                      onClick={() => echanger(menu.cle)}>
                <span className="recon-outil__bulle">Échanger avec les agents</span>
                <IcoEchanger />
              </button>
              {d.marque && (
                <button type="button" className="recon-outil recon-outil--danger"
                        onClick={() => retirer(menu.cle)}>
                  <span className="recon-outil__bulle">Retirer l'annotation</span>
                  <IcoRetirer />
                </button>
              )}
              {/* Détail et chiffrage valent pour un local COMME pour une pièce
                  d'ensemble : une pièce a sa propre aire et son périmètre. Le
                  tiroir vise le local par son rang, la pièce par sa clé. */}
              <span className="recon-barre__sep" aria-hidden="true" />
              <button type="button" className="recon-outil"
                      onClick={() => {
                        if (d.estPiece || d.estEscalier || d.estRepere) {
                          poserActif(null); poserDetailCle(d.cle);
                        } else if (d.rang != null) {
                          poserDetailCle(null); poserActif(d.rang);
                        }
                        poserMenu(null);
                      }}>
                <span className="recon-outil__bulle">Voir le détail</span>
                <IcoDetail />
              </button>
              <button type="button" className="recon-outil"
                      onClick={() => {
                        poserChiffrer({
                          nom: libelleChiffrage(d.nom, d.cle, d.estPiece,
                                                d.estEscalier),
                          ...metreDe(d) });
                        poserMenu(null);
                      }}>
                <span className="recon-outil__bulle">Chiffrer</span>
                <IcoChiffrer />
              </button>
            </div>
          </>
        );
      })()}

      {editeur && (() => {
        const d = parCle.get(editeur.cle);
        if (!d) return null;
        const estNote = editeur.mode === "note";
        const quoi = d.estEscalier ? "escalier" : d.estPiece ? "pièce" : "local";
        return (
          <>
            <div className="recon-barre-fond" onClick={() => poserEditeur(null)} />
            <div className="recon-editeur" style={{ left: editeur.x, top: editeur.y }}>
              <label className="recon-editeur__label">
                {estNote ? `Annoter « ${d.nomSource || d.nom || quoi} »`
                  : `Nommer « ${d.nomSource || `${quoi} sans nom`} »`}
              </label>
              {estNote ? (
                <textarea className="recon-editeur__champ" rows={3} value={brouillon}
                          autoFocus onChange={(e) => poserBrouillon(e.target.value)}
                          placeholder="Note du métreur…" />
              ) : (
                <input className="recon-editeur__champ" value={brouillon} autoFocus
                       onChange={(e) => poserBrouillon(e.target.value)}
                       placeholder="Nouveau nom (vide = nom d'origine)"
                       onKeyDown={(e) => { if (e.key === "Enter") void enregistrer(); }} />
              )}
              {erreurEdit && <span className="recon-editeur__err">{erreurEdit}</span>}
              <div className="recon-editeur__pied">
                <button type="button" className="bouton bouton--discret"
                        onClick={() => poserEditeur(null)}>Annuler</button>
                <button type="button" className="bouton bouton--primaire"
                        disabled={enreg} onClick={() => void enregistrer()}>
                  {enreg ? "…" : "Enregistrer"}
                </button>
              </div>
            </div>
          </>
        );
      })()}

      {localActif && (
        <TiroirLocal local={localActif} chevauchements={chevauchements}
                     fermer={() => poserActif(null)}
                     chiffrer={() => poserChiffrer({
                       nom: libelleChiffrage(localActif.nom, localActif.cle),
                       ...metreDe({
                         aire: localActif.aire_m2, perimetre: localActif.perimetre_m,
                         aireDeclaree: localActif.aire_declaree_m2,
                         estInfere: localActif.source === "contour inféré",
                         estEcritSeule: localActif.source === "surface écrite seule",
                       }) })} />
      )}

      {detailCle && (() => {
        const d = parCle.get(detailCle);
        if (!d || !(d.estPiece || d.estEscalier || d.estRepere)) return null;
        // Une pièce, un escalier ou un repère vus comme un local MINIMAL : le
        // tiroir montre ses mesures et son annotation ; parois, ouvertures et
        // sous-pièces n'existent pas pour eux, et leurs sections se replient. Un
        // repère n'a NI aire ni contour : le tiroir masque le bloc d'aires et
        // porte les hauteurs écrites à côté.
        const local: Local = {
          rang: -1, nom: d.nom, nom_source: d.nomSource, renomme: d.renomme,
          annotation: d.annotation, wkt: d.wkt ?? "", aire_m2: d.aire ?? 0,
          perimetre_m: d.perimetre, noms_dedans: d.noms ?? [],
          nommage: d.renomme ? "metreur" : (d.nomSource ? "deterministe" : "anonyme"),
          hauteurs_etiquetees: d.estRepere ? (d.hauteurs ?? []) : undefined,
          incertitudes: d.cloisonManquante
            ? ["Cloison manquante — plusieurs locaux dans une même pièce."]
            : d.estRepere
              ? [(d.estRampe ? "Rampe" : "Espace")
                 + " nommé au plan, sans contour ni surface dessinés — "
                 + "géométrie à établir (aucun métré fabriqué)."]
              : [],
        };
        return (
          <TiroirLocal local={local} estPiece={d.estPiece}
                       estEscalier={d.estEscalier} estRepere={d.estRepere}
                       traits={d.traits ?? null}
                       chevauchements={[]} fermer={() => poserDetailCle(null)}
                       chiffrer={() => poserChiffrer({
                         nom: libelleChiffrage(d.nom, d.cle, d.estPiece,
                                               d.estEscalier),
                         aire: d.aire, perimetre: d.perimetre })} />
        );
      })()}

      {chiffrer && (
        <Chiffrage affaireId={affaireId} local={chiffrer.nom || "Local"}
                   aire={chiffrer.aire} perimetre={chiffrer.perimetre}
                   fermer={() => poserChiffrer(null)} />
      )}
    </div>
  );
}

// ── La légende, qui commande les couches ────────────────────────────────────

function Legende({ couches, basculer }: {
  couches: Couches; basculer: (k: keyof Couches) => void;
}) {
  return (
    <div className="recon-legende" role="group" aria-label="Couches du plan">
      {LEGENDE.map(({ clef, libelle, classe }) => (
        <button key={clef} type="button"
                className={`recon-leg${couches[clef] ? "" : " recon-leg--off"}`}
                aria-pressed={couches[clef]} onClick={() => basculer(clef)}>
          <span className={`recon-leg__pastille ${classe}`} aria-hidden="true" />
          {libelle}
        </button>
      ))}
    </div>
  );
}

// ── Le bilan d'un niveau (l'événement « bilan » du flux) ─────────────────────

function BilanNiveau({ bilan }: { bilan: Bilan }) {
  const c = bilan.comptes || {};
  return (
    <div className="recon-bilan">
      <Mesure n={bilan.surface_relevee_m2} mot="m² relevés" fixe={1} />
      {bilan.surface_declaree_m2 != null && (
        <Mesure n={bilan.surface_declaree_m2} mot="m² déclarés au plan" fixe={1} />
      )}
      {bilan.ecart_m2 != null && (
        <Mesure n={bilan.ecart_m2} mot="m² d'écart" fixe={1}
                ton={Math.abs(bilan.ecart_m2) > 0.5 ? "alerte" : undefined} />
      )}
      <Mesure n={c.deterministe} mot="nommés au plan" />
      <Mesure n={c.inference} mot="nommés par voisinage" ton={c.inference ? "doux" : undefined} />
      <Mesure n={c.anonyme} mot="sans nom" ton={c.anonyme ? "doux" : undefined} />
      {bilan.avertissements_non_leves ? (
        <Mesure n={bilan.avertissements_non_leves} mot="réserves non levées" ton="alerte" />
      ) : null}
    </div>
  );
}
function Mesure({ n, mot, fixe = 0, ton }: {
  n?: number | null; mot: string; fixe?: number; ton?: "alerte" | "doux";
}) {
  if (n == null) return null;
  return (
    <div className={"recon-mesure" + (ton ? ` recon-mesure--${ton}` : "")}>
      <span className="recon-mesure__n nombre">{n.toFixed(fixe).replace(".", ",")}</span>
      <span className="recon-mesure__mot">{mot}</span>
    </div>
  );
}

// ── Le tiroir de détail d'un local ──────────────────────────────────────────

function TiroirLocal({ local, chevauchements, fermer, chiffrer, estPiece,
                      estEscalier, estRepere, traits }: {
  local: Local; chevauchements: Chevauchement[]; fermer: () => void;
  chiffrer: () => void; estPiece?: boolean; estEscalier?: boolean;
  estRepere?: boolean; traits?: number | null;
}) {
  const parois = local.parois || [];
  const ouvertures = local.ouvertures || [];
  const pieces = local.pieces || [];
  const de = estEscalier ? "de l'escalier" : estRepere ? "du repère"
    : estPiece ? "de la pièce" : "du local";
  const ce = estEscalier ? "cet escalier" : estRepere ? "ce repère"
    : estPiece ? "cette pièce" : "ce local";
  const sansNom = estEscalier ? "Escalier sans nom" : estRepere ? "Repère sans nom"
    : estPiece ? "Pièce sans nom" : "Local sans nom";
  const litiges = chevauchements.filter(
    (c) => c.a_rang === local.rang || c.b_rang === local.rang);
  return (
    <aside className="tiroir recon-tiroir"
           aria-label={`Détail ${de} ${local.nom}`}>
      <div className="carte__tete">
        <div className="pile pile--serree" style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ margin: 0 }}>{local.nom || sansNom}</h3>
          <span className="discret">
            <BadgeNommage nommage={local.nommage} origine={local.origine_du_nom}
                          estEscalier={estEscalier} />
            {local.calque ? <span className="mono"> · {local.calque}</span> : null}
          </span>
          {local.renomme && local.nom_source && (
            <span className="discret">à l'origine « {local.nom_source} »</span>
          )}
        </div>
        <button type="button" className="bouton bouton--discret" onClick={fermer}>Fermer</button>
      </div>

      <div className="tiroir__corps pile">
        {(local.source === "contour inféré"
          || local.source === "surface écrite seule") && (
          <Avis ton="attention" permanent>
            {local.source === "contour inféré"
              ? "Contour INFÉRÉ (aucune zone dessinée) — à valider avant de s'y "
                + "fier. L'aire écrite fait foi ; le périmètre calculé n'entre "
                + "pas au chiffrage tant que le contour n'est pas validé."
              : "Aucun contour dessiné ni inférable — surface écrite seule, "
                + "géométrie à établir. Posée à l'emplacement de son étiquette."}
          </Avis>
        )}
        {local.annotation && (
          <div className="recon-note">
            <span className="recon-note__tete">✎ Annotation du métreur</span>
            <p className="recon-note__corps">{local.annotation}</p>
          </div>
        )}
        {/* Un repère n'a NI aire ni contour : pas de bloc d'aires (rien à y
            écrire sans fabriquer un nombre). Ses hauteurs restent affichées. */}
        {!estRepere && (
          <div className="recon-aires">
            <AireLigne libelle="Aire relevée" v={local.aire_m2} />
            {local.aire_recalculee_m2 != null && (
              <AireLigne libelle="Aire recalculée" v={local.aire_recalculee_m2}
                         ton={local.aire_verifiee === false ? "alerte" : "ok"} />
            )}
            {local.aire_declaree_m2 != null && (
              <AireLigne libelle="Écrite au plan" v={local.aire_declaree_m2} />
            )}
            {local.perimetre_m != null && (
              <AireLigne libelle="Périmètre" v={local.perimetre_m} unite="m" />
            )}
            {traits != null && (
              <AireLigne libelle="Traits (dessin)" v={traits} unite="" />
            )}
          </div>
        )}

        {(local.incertitudes || []).length > 0 && (
          <div className="pile pile--serree">
            {local.incertitudes!.map((t, i) => (
              <Avis key={i} ton="attention" permanent>{t}</Avis>
            ))}
          </div>
        )}

        {litiges.length > 0 && (
          <Avis ton="erreur" permanent>
            Chevauche {litiges.map((c) =>
              `« ${c.a_rang === local.rang ? c.b : c.a} » (${fmt(c.aire_m2)} m²)`).join(", ")}.
          </Avis>
        )}

        {pieces.length > 0 && (
          <Section titre={`Pièces (${pieces.length})`}>
            <table className="tableau recon-table">
              <thead><tr><th>Pièce</th><th>Aire</th><th>Périmètre</th></tr></thead>
              <tbody>
                {pieces.map((p, i) => (
                  <tr key={i}>
                    <td>{p.nom || "—"}</td>
                    <td className="nombre">{fmt(p.aire_m2)} m²</td>
                    <td className="nombre">{fmt(p.perimetre_m)} m</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        )}

        {parois.length > 0 && (
          <Section titre={`Parois (${parois.length})`}>
            <table className="tableau recon-table">
              <thead><tr><th>Type</th><th>Long.</th><th>Mitoyen</th><th>Matériau</th></tr></thead>
              <tbody>
                {parois.map((p, i) => (
                  <tr key={i}>
                    <td>{typeParoi(p.type)}</td>
                    <td className="nombre">{fmt(p.longueur_m)} m</td>
                    <td className="nombre">{p.partage_m ? `${fmt(p.partage_m)} m` : "—"}</td>
                    <td className="tronque" title={p.materiau || ""}>{p.materiau || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        )}

        {ouvertures.length > 0 && (
          <Section titre={`Ouvertures (${ouvertures.length})`}>
            <table className="tableau recon-table">
              <thead><tr><th>Genre</th><th>Largeur</th><th>Hauteur</th></tr></thead>
              <tbody>
                {ouvertures.map((o, i) => (
                  <tr key={i}>
                    <td>{libelleOuverture(o.genre)}</td>
                    <td className="nombre">{fmt(o.largeur_m)} m</td>
                    <td className="nombre">{o.hauteur_m ? `${fmt(o.hauteur_m)} m` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        )}

        {(local.hauteurs_etiquetees || []).length > 0 && (
          <Section titre="Hauteurs">
            <div className="glossaire-valeurs">
              {local.hauteurs_etiquetees!.map((h, i) => (
                <span key={i} className="glossaire-valeur mono">
                  {h.genre || "h"} {fmt(h.valeur_m)} m
                </span>
              ))}
            </div>
          </Section>
        )}

        {(local.sous_zones || []).length > 0 && (
          <Section titre="Finitions relevées">
            <div className="glossaire-valeurs">
              {local.sous_zones!.map((z, i) => (
                <span key={i} className="glossaire-valeur">
                  {z.materiau || z.etiquette || "—"}
                  {z.aire_m2 != null ? ` · ${fmt(z.aire_m2)} m²` : ""}
                </span>
              ))}
            </div>
          </Section>
        )}

        <button type="button" className="bouton bouton--primaire" onClick={chiffrer}>
          Chiffrer {ce}
        </button>
      </div>
    </aside>
  );
}

function Section({ titre, children }: { titre: string; children: React.ReactNode }) {
  return (
    <div className="pile pile--serree">
      <h4 className="recon-section">{titre}</h4>
      <div className="defilant">{children}</div>
    </div>
  );
}
function AireLigne({ libelle, v, unite = "m²", ton }: {
  libelle: string; v?: number | null; unite?: string; ton?: "ok" | "alerte";
}) {
  return (
    <div className={"recon-aire" + (ton ? ` recon-aire--${ton}` : "")}>
      <span className="discret">{libelle}</span>
      <span className="nombre">{v != null ? `${fmt(v)} ${unite}` : "—"}</span>
    </div>
  );
}
function BadgeNommage({ nommage, origine, estEscalier }: {
  nommage?: string; origine?: string; estEscalier?: boolean;
}) {
  // « au plan » ne vaut que pour un local/pièce nommé par l'architecte ; un
  // escalier, lui, n'est jamais étiqueté au plan.
  const auPlan = estEscalier ? "" : " au plan";
  const t = nommage === "metreur" ? "Renommé par le métreur"
    : nommage === "deterministe" ? `Nommé${auPlan}`
      : nommage === "inference" ? "Nommé par voisinage (à relire)"
        : `Sans nom${auPlan}`;
  return <span className={`recon-nom recon-nom--${nommage || "anonyme"}`} title={origine}>{t}</span>;
}

// ── Petits utilitaires ──────────────────────────────────────────────────────

function fmt(v?: number | null): string {
  if (v == null) return "—";
  return (Math.round(v * 100) / 100).toString().replace(".", ",");
}
function libelleOuverture(genre?: string): string {
  return genre === "porte" ? "Porte" : genre === "fenetre" ? "Fenêtre"
    : genre === "baie" ? "Baie" : "Ouverture";
}
function typeParoi(type?: string): string {
  return type === "cloison" ? "Cloison" : type === "mur" ? "Mur" : type || "Paroi";
}

/** Exporte le plan en SVG autonome (styles concrets inlinés) ou en PNG. Les
 *  téléchargements du portail passent par un lien blob — la CSP de l'app les
 *  autorise (ce n'est pas un artefact). */
function exporter(svg: SVGSVGElement, niveau: string, format: "svg" | "png") {
  const style = document.createElement("style");
  const jeton = (n: string) =>
    getComputedStyle(document.documentElement).getPropertyValue(n).trim() || "#000";
  // On fige les couleurs de thème : un SVG posé sur un bureau n'a pas nos tokens.
  style.textContent = `
    .recon-local__contour{fill:${jeton("--mo-surface")};fill-opacity:.92;stroke:${jeton("--mo-text-faint")};stroke-width:1}
    .recon-local__contour--doute{fill:${jeton("--mo-warning-tint")};stroke:${jeton("--mo-warning")}}
    .recon-local__contour--infere{fill:${jeton("--mo-accent-tint")};fill-opacity:.45;stroke:${jeton("--mo-accent")};stroke-dasharray:5 4}
    .recon-local__ancre{fill:${jeton("--mo-warning-tint")};stroke:${jeton("--mo-warning")};stroke-width:1.4;stroke-dasharray:3 2}
    .recon-local__piece{fill:none;stroke:${jeton("--mo-text-faint")};stroke-width:.7;stroke-dasharray:3 3}
    .recon-paroi{fill:none;stroke:${jeton("--mo-text")};stroke-width:2.2;stroke-linecap:round;stroke-linejoin:round}
    .recon-paroi--cloison{stroke:${jeton("--mo-text-muted")};stroke-width:1.2}
    .recon-ouverture{fill:none;stroke-width:3.4;stroke-linecap:round}
    .recon-ouverture--porte{stroke:${jeton("--mo-accent")}}
    .recon-ouverture--fenetre{stroke:${jeton("--mo-info")};stroke-width:2.4;stroke-opacity:.75}
    .recon-ouverture--baie,.recon-ouverture--inconnu{stroke:${jeton("--mo-text-muted")};stroke-width:2.4}
    .recon-ouverture--point{fill:${jeton("--mo-surface")};stroke-width:1.4}
    .recon-escalier{fill:none;stroke:${jeton("--mo-text-muted")};stroke-width:1;stroke-opacity:.85;stroke-linecap:round;stroke-linejoin:round}
    .recon-escalier__hit{fill:none;stroke:none;pointer-events:none}
    .recon-marque{fill:${jeton("--mo-info")};stroke:${jeton("--mo-surface")};stroke-width:1.4}
    .recon-marque--nom{fill:${jeton("--mo-accent")}}
    .recon-repere__pt{fill:${jeton("--mo-surface")};fill-opacity:.85;stroke:${jeton("--mo-info")};stroke-width:1.4;stroke-dasharray:3 2}
    .recon-repere__pt--rampe{stroke:${jeton("--mo-accent")}}
    .recon-repere__txt{fill:${jeton("--mo-text")};font-weight:600;paint-order:stroke;stroke:${jeton("--mo-surface")};stroke-width:3px;stroke-linejoin:round;dominant-baseline:middle}
    .recon-texte__pt{fill:none;stroke:none}
    .recon-chevauchement{fill:${jeton("--mo-error")};fill-opacity:.18;stroke:${jeton("--mo-error")};stroke-width:1}`;
  const clone = svg.cloneNode(true) as SVGSVGElement;
  clone.insertBefore(style, clone.firstChild);
  clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  const w = 1600, h = Math.round(w * (svg.clientHeight || 900) / (svg.clientWidth || 1600));
  clone.setAttribute("width", String(w));
  clone.setAttribute("height", String(h));
  const texte = new XMLSerializer().serializeToString(clone);
  const nom = `plan-${niveau.replace(/[^a-z0-9]+/gi, "-")}`;

  if (format === "svg") {
    telecharger(`${nom}.svg`, new Blob([texte], { type: "image/svg+xml" }));
    return;
  }
  const img = new Image();
  img.onload = () => {
    const c = document.createElement("canvas");
    c.width = w; c.height = h;
    const ctx = c.getContext("2d");
    if (!ctx) return;
    ctx.fillStyle = jeton("--mo-surface-inset") || "#fff";
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    c.toBlob((b) => b && telecharger(`${nom}.png`, b), "image/png");
  };
  img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(texte);
}
function telecharger(nom: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = nom;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
